import React, { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { Tooltip, Modal, Box, TextField } from '@mui/material'
import Swal from 'sweetalert2'
import Pusher from 'pusher-js'
import { Link } from 'react-router-dom'
import update from 'immutability-helper'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import AppLayout from '../../components/Layouts/AppLayout'
import TableSharedCustom from '../../components/TableSharedCustom'
import Loading from '../../components/Loading'
import AddButton from '../../components/AddButton'
import style from '../../styles/style'
import { useAuth } from '../../hooks/auth'

const UnitsOfMeasure = () => {

    const { t } = useTranslation()
    const { config, pusher } = useStateContext()
    const { user } = useAuth({ middleware: 'guest' })

    const [codes, setCodes] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [open, setOpen] = useState(false)
    const [code, setCode] = useState('')
    const [description, setDescription] = useState('')
    const [searchCode, setSearchCode] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            let filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.uom_code_no || ''
        } else {
            return ''
        }
    })
    const [model, setModel] = useState({
        actions: true,
        code: true,
        description: true
    })

    useEffect(() => {
        getTableView()
    }, [])

    const getTableView = async () => {
        setIsLoading(true)
        await axios.get(`/api/get-table-view?view_name=units_of_measure_table&user_id=${user?.id}`, config)
            .then(res => {
                const model = res.data
                if (Object.keys(model?.success?.data).length > 0) {
                    setModel(model.success.data)
                }
                setIsLoading(false)
            })
    }

    useEffect(() => {

        const channeldelete = pusher.subscribe(`${localStorage.getItem('client_id')}-unitofmeasure-deleted`)
        const channelcreate = pusher.subscribe(`${localStorage.getItem('client_id')}-unitofmeasure-created`)
        const channelupdate = pusher.subscribe(`${localStorage.getItem('client_id')}-unitofmeasure-updated`)

        channeldelete.bind(`${localStorage.getItem('client_id')}-unitofmeasure-deleted-event`, data => {
            getCodes()
        })

        channelcreate.bind(`${localStorage.getItem('client_id')}-unitofmeasure-created-event`, data => {
            getCodes()
        })

        channelupdate.bind(`${localStorage.getItem('client_id')}-unitofmeasure-updated-event`, data => {
            getCodes()
        })

        return (() => {
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-unitofmeasure-deleted`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-unitofmeasure-created`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-unitofmeasure-updated`)
        })
    }, [])

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '13') {
                if (open) createGradeCode()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [open, code, description]);



    useEffect(() => {
        getCodes()
    }, [])

    const handleOpen = () => setOpen(true)
    const handleClose = () => setOpen(false)

    const getCodes = async () => {
        setIsLoading(true)
        await axios.get(`/api/units-of-measure`, config)
            .then(res => {
                const codes = res.data
                setCodes(codes)
                setIsLoading(false)
            })
    }

    const createGradeCode = async () => {
        setIsLoading(true)
        const formData = {}

        formData['code'] = code
        formData['description'] = description

        await axios.post(`/api/units-of-measure`, formData, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data.success.message
            })
            setCode('')
            setDescription('')
            setIsLoading(false)
            handleClose()
        }).catch(({ response }) => {
            if (response.status === 422) {
                Swal.fire({
                    text: response.data.message,
                    icon: "error",
                    customClass: 'error',
                    showCloseButton: true,
                    iconColor: '#FF0000'
                })
            } else {
                Swal.fire({
                    text: response.data.message,
                    icon: "error",
                    customClass: 'error',
                    showCloseButton: true,
                    iconColor: '#FF0000'
                })
            }
            setIsLoading(false)
        })
    }


    const columns = [
        {
            field: 'code',
            headerName: t('code'),
            flex: 1
        },
        {
            field: 'description',
            headerName: t('description'),
            flex: 1
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.5,
            cellClassName: 'padding-0',
            renderCell: (params) => <UnitsOfMeasureActions params={params} config={config} setIsLoading={setIsLoading} />
        }
    ]

    const filtered = codes.filter(data => {
        if (searchCode === null)
            return data
        if (data.code.toLocaleLowerCase().includes(searchCode.toLocaleLowerCase()))
            return data
    })

    const createFilterArray = (no) => {
        if (localStorage.getItem('filters') === null) {
            let filter = [{}]
            localStorage.setItem('filters', JSON.stringify(filter))
        }

        let filters = JSON.parse(localStorage.getItem('filters'))

        filters[0].uom_code_no = no

        localStorage.setItem('filters', JSON.stringify(filters))
    }

    useEffect(() => {
        createFilterArray(searchCode)
    }, [searchCode])

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout showFooter={!open}>
                <div className='p-5 pt-0'>
                    <div className='pb-5 bg-white mb-2 rounded-md'>
                        <div className='flex justify-between items-center p-5 border-b'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('units_of_measure_list')}</p>
                            <Tooltip disableInteractive title={t('create_units_of_measure')} placement='bottom'>
                                <div>
                                    <AddButton onClick={handleOpen}><i className="fa-solid fa-plus"></i></AddButton>
                                </div>
                            </Tooltip>
                        </div>
                        <div className='border-b pb-5 w-full'>
                            <div className='flex justify-between items-end w-1/2'>
                                <div className='px-5 pt-5 w-full'>
                                    <div className='flex justify-between items-center search'>
                                        <input type="text" placeholder={t('searchUOM')} className='w-full bg-[#f5f5f5] border-0 focus:ring-0 p-0 font-bold place' value={searchCode} onChange={(e) => setSearchCode(e.target.value)} />
                                        <i className="fa-solid fa-magnifying-glass" style={{ color: 'rgba(0,0,0,.54)' }}></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='pt-3'>
                            <TableSharedCustom items={filtered} columns={columns} columnVisibilityModel={model} setModel={setModel} view='units_of_measure' />
                        </div>
                    </div>

                </div>
            </AppLayout>

            <Modal open={open} onClose={handleClose}>
                <Box sx={{...style, overflowX: 'hidden'}}>
                    <div className='flex border-b justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                        <div className='flex items-center justify-between w-full leading-none'>
                            <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#336195] text-[35px] mr-4'>•</span> {t('create_units_of_measure')}</p>
                            <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                                <button onClick={handleClose}><i className="fa-solid fa-plus"></i></button>
                            </div>
                        </div>
                    </div>
                    <div className='w-full bg-white h-auto min-h-full'>
                        <div className='bg-white p-6 w-full h-fit'>
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('code')} *</p>
                                <div className='w-full'>
                                    <TextField
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '9px !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        type="text" className='w-full' name="drop_trailer_no" value={code} onChange={(e) => { setCode(e.target.value) }} />
                                </div>
                            </div>
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('description')} *</p>
                                <div className='w-full'>
                                    <TextField
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '9px !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        type="text" className='w-full' name="drop_trailer_no" value={description} onChange={(e) => { setDescription(e.target.value) }} />
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex justify-end px-6 pb-14'>
                            <button type="button" onClick={createGradeCode} className=' text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#DBA34B] hover:bg-[#CF7300] disabled:bg-[#B8B7BC]'>
                                <span className='pr-2'><i className="fa-solid fa-plus"></i></span> {t('create')}
                            </button>
                        </div>
                    </div>
                </Box>
            </Modal>
        </>
    )
}

export default UnitsOfMeasure

const UnitsOfMeasureActions = (params) => {

    const { t } = useTranslation()

    const deleteItems = async (id) => {
        const isConfirm = await Swal.fire({
            title: t('title_delete') + t('del_uom') + params.params.row.code + "?",
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,

            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            return result.isConfirmed
        })

        if (!isConfirm) {
            return
        }
        await axios.delete(`/api/units-of-measure/${id}`, params.config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data.success.message
            })
        }).catch(({ response: { data } }) => {
            Swal.fire({
                text: data.message,
                icon: "error",
                customClass: 'error',
                showCloseButton: true,
                iconColor: '#FF0000'
            })
        })
    }


    return (
        <>
            <div className='flex justify-between'>
                <Tooltip disableInteractive title={t('edit')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <Link to={`${params.params.row.id}`}><span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"><i className="fa-solid fa-pencil"></i></span></Link>
                    </div>
                </Tooltip>
                <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteItems(params.params.row.id)}><i className="fa-solid fa-trash"></i></span>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}

